import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanPlaceholder } from "@domain/project/floorplan/floorplan-placeholder";
import { FloorplanPlaceholderPositionKind } from "@domain/project/floorplan/floorplan-placeholder-position-kind";
import { DEFAULT_FLAME_DETECTOR_CONE_ROTATION } from "@lib/constants/flame_detector_defaults";
import { Exclude, Expose, Transform } from "class-transformer";
import { IsNumber, IsOptional, IsPositive, IsString } from "class-validator";
import { Subject } from "rxjs";
import { v4 as uuidv4 } from "uuid";

export class FloorplanFlameDetectorPlaceholder extends FloorplanPlaceholder {
  @Exclude()
  DEFAULT_COVERAGE_AREA_LENGTH: number = 1;

  @IsNumber()
  @IsPositive({ message: "The value must be higher than 0" })
  @Expose({ name: "monitoringAreaRadius" })
  private _coverageArea: number;

  @IsString()
  @IsOptional()
  @Expose({ name: "sensititvity" })
  private _sensitivity?: string;

  @IsString()
  @IsOptional()
  @Expose({ name: "mountingHeight" })
  private _mountingHeight?: string;

  @IsNumber()
  @Expose({ name: "coneRotation" })
  @Transform((params) => params.value ?? DEFAULT_FLAME_DETECTOR_CONE_ROTATION, { toClassOnly: true })
  private _coneRotation: number = DEFAULT_FLAME_DETECTOR_CONE_ROTATION;

  @IsNumber()
  private _referenceCoverageAreaLength: number = this.DEFAULT_COVERAGE_AREA_LENGTH;

  @Exclude()
  public coverageAreaLengthChanged: Subject<{ length: number }>;

  @Exclude()
  referenceCoverageAreaLengthChanged: Subject<{ referenceLength: number }>;

  constructor(floorplan: Floorplan, id: string, x: number, y: number, size: number, coverageAreaLength: number, notes?: string) {
    super(floorplan, id, x, y, size, notes);
    this._coverageArea = coverageAreaLength;
    this.coverageAreaLengthChanged = new Subject();
    this.referenceCoverageAreaLengthChanged = new Subject();
    this.updateCoverageAreaLengthByReferenceMeasure();
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.flameDetectorsItemTextVisible;
  }

  override init(floorplan: Floorplan) {
    super.init(floorplan);
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanItem) | undefined {
    const coverageArea = this.coverageArea;
    const size = this.size;

    return (x: number, y: number) => FloorplanFlameDetectorPlaceholder.create(this._floorplan, x, y, size, coverageArea);
  }

  protected override copySpecificDuplicateStateTo(): ((duplicate: FloorplanItem) => void) | undefined {
    const sensitivity = this.sensitivity;
    const mountingHeight = this.mountingHeight;
    const products = this.capturePlaceholderProducts();

    return (duplicate: FloorplanItem) => {
      const duplicatePlaceholder = duplicate as FloorplanFlameDetectorPlaceholder;
      this.copyPlaceholderProductsTo(duplicatePlaceholder, products);
      duplicatePlaceholder._sensitivity = sensitivity;
      duplicatePlaceholder._mountingHeight = mountingHeight;
    };
  }

  protected override afterDuplicateAdded(): ((duplicate: FloorplanItem) => void) | undefined {
    const afterDuplicateAdded = super.afterDuplicateAdded();
    const referenceCoverageAreaLength = this.referenceCoverageAreaLength;

    return (duplicate: FloorplanItem) => {
      const duplicatePlaceholder = duplicate as FloorplanFlameDetectorPlaceholder;
      afterDuplicateAdded?.(duplicatePlaceholder);

      if (this._floorplan.referenceMeasure) {
        duplicatePlaceholder.updateCoverageAreaLength(referenceCoverageAreaLength);
      }
    };
  }

  override getDuplicateTargetCollection() {
    return this._floorplan.flameDetectorPlaceholders;
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate as FloorplanFlameDetectorPlaceholder,
        this._floorplan.flameDetectorPlaceholders,
        FloorplanEventType.FLAME_DETECTOR_PLACEHOLDER_ADDED,
        () => this.refreshPlaceholderPositionIds(),
        afterDuplicateAdded as ((duplicate: FloorplanFlameDetectorPlaceholder) => void) | undefined,
      );
  }

  override delete() {
    this.coverageAreaLengthChanged.unsubscribe();
    this._floorplan.deleteFlameDetectorPlaceholder(this);
  }

  get coverageArea(): number {
    return this._coverageArea;
  }

  set coverageArea(value: number) {
    if (this._floorplan?.referenceMeasure) {
      value = this.calculateCoverageArea();
    }
    this._coverageArea = value;
    this._floorplan?.publishUpdate(FloorplanEventType.ITEM_UPDATED, this);
  }

  get referenceCoverageAreaLength() {
    return this._referenceCoverageAreaLength;
  }

  get sensitivity(): string | undefined {
    return this._sensitivity;
  }

  set sensitivity(value: string) {
    this._sensitivity = value;
  }

  get mountingHeight(): string | undefined {
    return this._mountingHeight;
  }

  set mountingHeight(value: string) {
    this._mountingHeight = value;
  }

  get coneRotation(): number {
    return this._coneRotation;
  }

  set coneRotation(value: number) {
    this._coneRotation = value;
    this._floorplan?.publishUpdate(FloorplanEventType.ITEM_UPDATED, this);
  }

  isReferenceMeasureSet() {
    if (this._floorplan) return !!this._floorplan.referenceMeasure;
    return false;
  }

  updateCoverageAreaLength(radius: number) {
    if (this.isReferenceMeasureSet()) {
      this._referenceCoverageAreaLength = radius;
      this._coverageArea = radius * this._floorplan.getReferenceScale();
    }
    this._floorplan.publishUpdate(FloorplanEventType.COVERAGE_AREA_LENGTH_UPDATED, this);
  }

  updateCoverageAreaLengthByReferenceMeasure(): void {
    if (this.isReferenceMeasureSet()) {
      this._coverageArea = this.calculateCoverageAreaByReference();
      this._floorplan.publishUpdate(FloorplanEventType.COVERAGE_AREA_LENGTH_UPDATED, this);
    }
  }

  public isCoverageAreaScaleAble(): boolean {
    return !this._floorplan.referenceMeasure;
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshFlameDetectorsLock();
  }

  override getPlaceholderPositionKind(): FloorplanPlaceholderPositionKind | undefined {
    return FloorplanPlaceholderPositionKind.FLAME_DETECTOR;
  }

  static create(
    floorplan: Floorplan,
    x: number,
    y: number,
    size: number,
    coverageAreaLength: number,
  ): FloorplanFlameDetectorPlaceholder {
    return new FloorplanFlameDetectorPlaceholder(floorplan, uuidv4(), x, y, size, coverageAreaLength);
  }

  private calculateCoverageAreaByReference(): number {
    return Math.round(this.referenceCoverageAreaLength * this._floorplan.getReferenceScale() * 100) / 100;
  }

  private calculateCoverageArea(): number {
    return Math.round((this.coverageArea / this._floorplan.getReferenceScale()) * 100) / 100;
  }
}
